export type ControlGlyphScheme = "keyboard" | "xbox" | "playstation" | "switch";

interface ControlLabels {
  move: string;
  jump: string;
  switchCat: string;
  pause: string;
  next: string;
  skip: string;
  aura: string;
  shadow: string;
  purr: string;
  turnStart: string;
  rhythmHit: string;
  anchor: string;
  leap: string;
  interact: string;
  leftPaw: string;
  rightPaw: string;
  biscuitStart: string;
  resonantDrop: string;
  chronosBubble: string;
}

const LABELS: Record<Exclude<ControlGlyphScheme, "keyboard">, ControlLabels> = {
  xbox: {
    move: "[L-stick]",
    jump: "[A]",
    switchCat: "[View]",
    pause: "[Menu]",
    next: "[A]",
    skip: "[B]/[Menu]",
    aura: "[X]",
    shadow: "[B]",
    purr: "[RB]",
    turnStart: "[X]",
    rhythmHit: "[Y]",
    anchor: "[LB]",
    leap: "[B]",
    interact: "[LT]",
    leftPaw: "[LT]",
    rightPaw: "[RT]",
    biscuitStart: "[LT]/[RT]",
    resonantDrop: "[RB] + [LT]",
    chronosBubble: "[RS] + [RT]",
  },
  playstation: {
    move: "[L-stick]",
    jump: "[Cross]",
    switchCat: "[Share]",
    pause: "[Options]",
    next: "[Cross]",
    skip: "[Circle]/[Options]",
    aura: "[Square]",
    shadow: "[Circle]",
    purr: "[R1]",
    turnStart: "[Square]",
    rhythmHit: "[Triangle]",
    anchor: "[L1]",
    leap: "[Circle]",
    interact: "[L2]",
    leftPaw: "[L2]",
    rightPaw: "[R2]",
    biscuitStart: "[L2]/[R2]",
    resonantDrop: "[R1] + [L2]",
    chronosBubble: "[R3] + [R2]",
  },
  switch: {
    move: "[L-stick]",
    jump: "[B]",
    switchCat: "[-]",
    pause: "[+]",
    next: "[B]",
    skip: "[A]/[+]",
    aura: "[Y]",
    shadow: "[A]",
    purr: "[R]",
    turnStart: "[Y]",
    rhythmHit: "[X]",
    anchor: "[L]",
    leap: "[A]",
    interact: "[ZL]",
    leftPaw: "[ZL]",
    rightPaw: "[ZR]",
    biscuitStart: "[ZL]/[ZR]",
    resonantDrop: "[R] + [ZL]",
    chronosBubble: "[R-stick] + [ZR]",
  },
};

export type ControlAction = keyof ControlLabels;

/**
 * Controller button label(s) for an action, without brackets — e.g.
 * ("anchor", "xbox") → ["LB"]; ("resonantDrop", "xbox") → ["RB", "LT"].
 * Keyboard keys depend on which cat and input mode, so they come from
 * tutorialCues.keyboardKeys instead.
 */
export function controllerLabels(
  action: ControlAction,
  scheme: Exclude<ControlGlyphScheme, "keyboard">,
): string[] {
  return [...LABELS[scheme][action].matchAll(/\[([^\]]+)\]/g)].map((m) => m[1]);
}

function readGamepads(): readonly (Gamepad | null)[] {
  if (typeof navigator === "undefined" || typeof navigator.getGamepads !== "function") {
    return [];
  }
  try {
    return navigator.getGamepads();
  } catch {
    return [];
  }
}

export function detectControlGlyphScheme(
  gamepads: readonly (Gamepad | null)[] = readGamepads(),
): ControlGlyphScheme {
  const pad = gamepads.find((candidate) => {
    if (!candidate) return false;
    return candidate.connected !== false;
  });
  if (!pad) return "keyboard";

  const id = pad.id.toLowerCase();
  if (id.includes("nintendo") || id.includes("joy-con") || id.includes("switch") || id.includes("pro controller")) {
    return "switch";
  }

  if (id.includes("xbox") || id.includes("xinput")) {
    return "xbox";
  }

  if (
    id.includes("dualsense")
    || id.includes("dualshock")
    || id.includes("playstation")
    || id.includes("wireless controller")
    || /\bps[345]\b/.test(id)
  ) {
    return "playstation";
  }

  return "xbox";
}

export function isAnyGamepadButtonPressed(buttonIndices: readonly number[]): boolean {
  for (const pad of readGamepads()) {
    if (!pad || pad.connected === false) continue;
    for (const index of buttonIndices) {
      if (pad.buttons[index]?.pressed) return true;
    }
  }
  return false;
}

export function formatControlText(
  text: string,
  scheme: ControlGlyphScheme = detectControlGlyphScheme(),
): string {
  if (scheme === "keyboard") return text;

  const l = LABELS[scheme];
  let out = text;

  out = out
    .replace(/Space or Enter: next\s+\|\s+Esc: skip cutscene/g, `${l.next}: next  |  ${l.skip}: skip cutscene`)
    .replace(/WASD to move/g, `${l.move} to move`)
    .replace(/WASD \+ Space/g, `${l.move} + ${l.jump}`)
    .replace(/Arrows \+ Shift\/Enter/g, `${l.move} + ${l.jump}`)
    .replace(/Arrows \+ Shift/g, `${l.move} + ${l.jump}`)
    .replace(/Space to jump/g, `${l.jump} to jump`)
    .replace(/Tab to switch cats/g, `${l.switchCat} to switch cats`)
    .replace(/Esc to pause/g, `${l.pause} to pause`)
    .replace(/F \+ M/g, l.resonantDrop)
    .replace(/G \+ N/g, l.chronosBubble)
    .replace(/3 Pneumatic/g, `${l.anchor} Pneumatic`)
    .replace(/4 Overclocked/g, `${l.leap} Overclocked`)
    .replace(/1 spin/g, `${l.turnStart} spin`)
    .replace(/2 rhythm-hit/g, `${l.rhythmHit} rhythm-hit`)
    .replace(/,\s*wind/g, `${l.interact} wind`)
    .replace(/\bcomma as Basil\b/g, `${l.interact} as Basil`)
    .replace(/\bpress B to knead\b/gi, `press ${l.biscuitStart} to knead`)
    .replace(/\bAlternate A and D to knead\b/g, `Alternate ${l.leftPaw} and ${l.rightPaw} to knead`)
    .replace(/\bHold any paw key\b/g, `Hold ${l.biscuitStart}`)
    .replace(/\bAbilities are shared on the keyboard\b/g, "Ability prompts follow each cat's controller");

  out = replaceVerbKey(out, "Q", l.aura);
  out = replaceVerbKey(out, "E", l.shadow);
  out = replaceVerbKey(out, "F", l.purr);
  out = replaceVerbKey(out, "1", l.turnStart);
  out = replaceVerbKey(out, "2", l.rhythmHit);
  out = replaceVerbKey(out, "3", l.anchor);
  out = replaceVerbKey(out, "4", l.leap);
  out = replaceVerbKey(out, ",", l.interact);

  out = out
    .replace(/\bWASD\b/g, l.move)
    .replace(/\bArrows\b/g, l.move)
    .replace(/\bSpace\b/g, l.jump)
    .replace(/\bEnter\b/g, l.jump)
    .replace(/\bShift\b/g, l.jump)
    .replace(/\bTab\b/g, l.switchCat)
    .replace(/\bEsc\b/g, l.pause)
    .replace(/\bQ\b/g, l.aura)
    .replace(/\bE\b/g, l.shadow)
    .replace(/\bF\b/g, l.purr)
    .replace(/\bM\b/g, l.interact)
    .replace(/\bG\b/g, scheme === "xbox" ? "[RS]" : "[R3]")
    .replace(/\bN\b/g, scheme === "xbox" ? "[RT]" : "[R2]")
    .replace(/\bkey 3\b/gi, l.anchor);

  return out;
}

function replaceVerbKey(text: string, key: string, label: string): string {
  const escaped = key === "," ? "," : key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(
    new RegExp(`\\b([Pp]ress|[Tt]ry|[Tt]ap|[Hh]old)\\s+${escaped}\\b`, "g"),
    `$1 ${label}`,
  );
}
